// ObjectiveModule.js - Version simplifiée sans concepts clés
// Module de coordination : Gestion de l'état global, navigation et intégration IA/Firebase

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Target, Lightbulb, GraduationCap, Briefcase, Heart, ArrowLeft, 
  Loader2, BookMarked, Home, DollarSign, Palmtree, Rocket, 
  FileSpreadsheet, TrendingUp, BookOpen, Zap, MessageCircle,
  CheckCircle, ChevronRight, RefreshCw, Sparkles, Send, Edit3,
  ChevronDown, ChevronUp, User, AlertCircle, ThumbsUp, Check
} from 'lucide-react';
import { doc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { askGPT } from '../components/OpenAIService';

// Import des modules fonctionnels existants
import { ObjectiveView } from './ObjectiveManagement';
import { ObjectiveSearchForm, ObjectiveSelection } from './ObjectiveDiscovery';
import ObjectifEditor from '../CP-Inspiration/ObjectifEditor';


// ============================================================================
// HOOK RESPONSIVE
// ============================================================================

const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = screenSize.width < 768;
  const isTablet = screenSize.width >= 768 && screenSize.width < 1024;
  const isDesktop = screenSize.width >= 1024;

  return { isMobile, isTablet, isDesktop, width: screenSize.width, height: screenSize.height };
};

// ============================================================================
// CONFIGURATION DES DOMAINES
// ============================================================================

const getDomainInfo = (domainName) => {
  const domainMap = {
    'Professionnel': { 
      bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200',
      icon: <Briefcase className="w-7 h-7 text-purple-600" />,
      iconSmall: <Briefcase className="w-5 h-5 text-purple-600" />
    },
    'Personnel': { 
      bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200',
      icon: <Target className="w-7 h-7 text-green-600" />,
      iconSmall: <Target className="w-5 h-5 text-green-600" />
    },
    'Educatif': { 
      bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200',
      icon: <GraduationCap className="w-7 h-7 text-blue-600" />,
      iconSmall: <GraduationCap className="w-5 h-5 text-blue-600" />
    },
    'Business': { 
      bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200',
      icon: <FileSpreadsheet className="w-7 h-7 text-indigo-600" />,
      iconSmall: <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
    },
    'Activités ludique et créative': { 
      bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200',
      icon: <Lightbulb className="w-7 h-7 text-green-600" />,
      iconSmall: <Lightbulb className="w-5 h-5 text-green-600" />
    },
    'Développement entrepreneurial': { 
      bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200',
      icon: <Rocket className="w-7 h-7 text-orange-600" />,
      iconSmall: <Rocket className="w-5 h-5 text-orange-600" />
    },
    'Formation et apprentissage': { 
      bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200',
      icon: <BookOpen className="w-7 h-7 text-indigo-600" />,
      iconSmall: <BookOpen className="w-5 h-5 text-indigo-600" />
    },
    'Évolution de carrière': { 
      bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200',
      icon: <TrendingUp className="w-7 h-7 text-violet-600" />,
      iconSmall: <TrendingUp className="w-5 h-5 text-violet-600" />
    },
    'Santé et bien-être': { 
      bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200',
      icon: <Heart className="w-7 h-7 text-emerald-600" />,
      iconSmall: <Heart className="w-5 h-5 text-emerald-600" />
    },
    'Objectifs Personnels': { 
      bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200',
      icon: <Target className="w-7 h-7 text-cyan-600" />,
      iconSmall: <Target className="w-5 h-5 text-cyan-600" />
    },
    'Voyages': { 
      bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200',
      icon: <Palmtree className="w-7 h-7 text-sky-600" />,
      iconSmall: <Palmtree className="w-5 h-5 text-sky-600" />
    },
    'Gestion immobilière': { 
      bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200',
      icon: <Home className="w-7 h-7 text-amber-600" />,
      iconSmall: <Home className="w-5 h-5 text-amber-600" />
    },
    'Finance et Banque': { 
      bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200',
      icon: <DollarSign className="w-7 h-7 text-emerald-600" />,
      iconSmall: <DollarSign className="w-5 h-5 text-emerald-600" />
    },
    'Activités sportives': { 
      bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200',
      icon: <Zap className="w-7 h-7 text-red-600" />,
      iconSmall: <Zap className="w-5 h-5 text-red-600" />
    }
  };

  return domainMap[domainName] || {
    bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200',
    icon: <Target className="w-7 h-7 text-gray-600" />,
    iconSmall: <Target className="w-5 h-5 text-gray-600" />
  };
};

// ============================================================================
// FONCTIONS DE GÉNÉRATION DE QUESTIONS GUIDÉES
// ============================================================================

const generateGuidedQuestionsForObjective = async (initialObjective, askGPT) => {
  const prompt = `
Analyse cet objectif utilisateur et génère exactement 3 questions pertinentes pour le préciser et l'enrichir :

Objectif initial : "${initialObjective}"

Tu dois générer 3 questions qui aideront l'utilisateur à clarifier son intention, son contexte et ses besoins spécifiques.

Pour chaque question, propose 4-6 choix de réponses concrètes et actionnables.

Réponds UNIQUEMENT avec un JSON valide dans ce format exact :
{
  "questions": [
    {
      "id": "question1",
      "question": "Question claire et directe ?",
      "category": "intention|context|specificity|timeline|resources|outcomes",
      "choices": [
        {"id": "choice1", "label": "Choix court", "description": "Description explicative"},
        {"id": "choice2", "label": "Choix court", "description": "Description explicative"}
      ]
    }
  ]
}

IMPORTANT : 
- Questions courtes et claires
- Choix variés et pertinents pour l'objectif donné
- Labels de choix : maximum 4-5 mots
- Descriptions : une phrase courte
- Exactement 3 questions
- JSON valide uniquement, pas de texte avant/après
`;

  try {
    const response = await askGPT(prompt);
    
    if (response) {
      const cleanResponse = response
        .replace(/```json|```/g, '')
        .replace(/^[^{]*/, '')
        .replace(/[^}]*$/, '')
        .trim();
      
      const parsed = JSON.parse(cleanResponse);
      
      if (parsed.questions && Array.isArray(parsed.questions) && parsed.questions.length === 3) {
        return parsed.questions;
      }
    }
    
    throw new Error('Format de réponse invalide');
  } catch (error) {
    console.error('Erreur génération questions:', error);
    
    // Questions de fallback génériques
    return [
      {
        id: "intention",
        question: "Quelle est votre intention principale ?",
        category: "intention",
        choices: [
          { id: 'learn', label: 'Apprendre', description: 'Acquérir des connaissances' },
          { id: 'find', label: 'Trouver', description: 'Rechercher des informations' },
          { id: 'compare', label: 'Comparer', description: 'Évaluer des options' },
          { id: 'solve', label: 'Résoudre', description: 'Trouver une solution' },
          { id: 'create', label: 'Créer', description: 'Développer quelque chose' }
        ]
      },
      {
        id: "context",
        question: "Dans quel contexte travaillez-vous ?",
        category: "context",
        choices: [
          { id: 'personal', label: 'Personnel', description: 'Développement personnel' },
          { id: 'professional', label: 'Professionnel', description: 'Cadre de travail' },
          { id: 'academic', label: 'Académique', description: 'Études ou recherche' },
          { id: 'entrepreneurial', label: 'Entrepreneurial', description: 'Projet d\'entreprise' }
        ]
      },
      {
        id: "priority",
        question: "Quel aspect vous préoccupe le plus ?",
        category: "specificity",
        choices: [
          { id: 'speed', label: 'Rapidité', description: 'Obtenir des résultats vite' },
          { id: 'quality', label: 'Qualité', description: 'Résultats de haute qualité' },
          { id: 'cost', label: 'Coût', description: 'Solution économique' },
          { id: 'simplicity', label: 'Simplicité', description: 'Approche simple' }
        ]
      }
    ];
  }
};

// ============================================================================
// FONCTION D'ENRICHISSEMENT AUTOMATIQUE
// ============================================================================

  const enrichObjectiveFromAnswers = async (currentObjective, answers, aiQuestions, askGPT) => {
  if (!currentObjective || Object.keys(answers).length === 0) return currentObjective;

  const answersText = aiQuestions
    .filter(q => answers[q.id])
    .map(q => {
      const answer = answers[q.id];
      if (answer.type === 'choices') {
        const labels = answer.data.map(choice => choice.label).join(' • ');
        return `${q.question} → ${labels}`;
      } else {
        // Type 'custom' 
        return `${q.question} → ${answer.data.label}`;
      }
    }).join('\n');

  const chatContext = '';

  const enrichmentPrompt = `
Objectif initial : "${currentObjective}"

Réponses aux questions de précision :
${answersText}${chatContext}

Reformule cet objectif en intégrant naturellement les précisions apportées. 
L'objectif enrichi doit :
- Rester concis (maximum 2 phrases)
- Intégrer les éléments de contexte pertinents
- Être plus actionnable que l'objectif initial

Réponds uniquement avec l'objectif reformulé, sans explication.
`;

  try {
    const enrichedObjective = await askGPT(enrichmentPrompt);
    if (enrichedObjective && enrichedObjective !== currentObjective) {
      return enrichedObjective;
    }
  } catch (error) {
    console.error('Erreur enrichissement objectif:', error);
  }
  
  return currentObjective;
};



// ============================================================================
// COMPOSANT QUESTION GUIDÉE AVEC SÉLECTION MULTIPLE
// ============================================================================

const AIGeneratedQuestion = ({ 
  question, 
  selectedChoices = [], // MODIFIÉ : array au lieu d'objet
  customText, 
  onChoiceSelect, 
  onCustomTextSubmit,
  isCompleted,
  questionIndex,
  totalQuestions,
  isMobile
}) => {
  const [isExpanded, setIsExpanded] = useState(!isCompleted);
  const [showCustomField, setShowCustomField] = useState(false);
  const [localCustomText, setLocalCustomText] = useState(customText || '');

  // MODIFIÉ : logique de sélection multiple
  const handleChoiceClick = (choice) => {
    const isSelected = selectedChoices.some(selected => selected.id === choice.id);
    let newSelection;
    
    if (isSelected) {
      // Désélectionner si déjà sélectionné
      newSelection = selectedChoices.filter(selected => selected.id !== choice.id);
    } else {
      // Ajouter à la sélection
      newSelection = [...selectedChoices, choice];
    }
    
    onChoiceSelect(newSelection);
    setShowCustomField(false);
    // Ne pas fermer automatiquement pour permettre la sélection multiple
  };

  const handleCustomSubmit = () => {
    if (localCustomText.trim()) {
      onCustomTextSubmit(localCustomText.trim());
      setShowCustomField(false);
      setIsExpanded(false);
    }
  };

  // AJOUTÉ : fonction pour valider la sélection multiple
  const handleValidateSelection = () => {
    setIsExpanded(false);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'intention': return <Target className="h-4 w-4" />;
      case 'context': return <Briefcase className="h-4 w-4" />;
      case 'specificity': return <Lightbulb className="h-4 w-4" />;
      case 'timeline': return <BookOpen className="h-4 w-4" />;
      case 'resources': return <DollarSign className="h-4 w-4" />;
      case 'outcomes': return <RefreshCw className="h-4 w-4" />;
      default: return <MessageCircle className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'intention': return 'text-blue-600 border-blue-200 bg-blue-50';
      case 'context': return 'text-green-600 border-green-200 bg-green-50';
      case 'specificity': return 'text-purple-600 border-purple-200 bg-purple-50';
      case 'timeline': return 'text-orange-600 border-orange-200 bg-orange-50';
      case 'resources': return 'text-emerald-600 border-emerald-200 bg-emerald-50';
      case 'outcomes': return 'text-pink-600 border-pink-200 bg-pink-50';
      default: return 'text-gray-600 border-gray-200 bg-gray-50';
    }
  };

  return (
    <div className={`border rounded-lg transition-all shadow-sm ${
      isCompleted ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50'
    }`}>
      {/* En-tête cliquable */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`p-2 rounded-lg mr-3 ${getCategoryColor(question.category)}`}>
              {getCategoryIcon(question.category)}
            </div>
            <div>
              <h3 className={`font-medium text-gray-800 ${isMobile ? 'text-sm' : ''}`}>
                {question.question}
              </h3>
              <p className="text-xs text-gray-500">Question {questionIndex + 1} sur {totalQuestions}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {isCompleted && <CheckCircle className="h-5 w-5 text-green-500" />}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>
        
        {/* MODIFIÉ : Affichage de la réponse si complétée et rideau fermé */}
        {isCompleted && !isExpanded && (
          <div className="mt-2 p-2 bg-white rounded border text-sm">
            <strong>Réponse :</strong> {
              selectedChoices.length > 0 
                ? selectedChoices.map(choice => choice.label).join(' • ') 
                : customText
            }
          </div>
        )}
      </div>

      {/* Contenu expansible */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Choix générés par l'IA */}
          <div className="grid gap-2 mb-3 mt-3">
            {question.choices.map((choice) => (
              <button
                key={choice.id}
                onClick={() => handleChoiceClick(choice)}
                className={`text-left p-3 rounded-lg border transition-all hover:border-blue-300 hover:bg-blue-50 ${
                  selectedChoices.some(selected => selected.id === choice.id)
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                    : 'border-gray-200'
                }`}
              >
                <div className="flex items-center">
                  {/* AJOUTÉ : Indicateur de sélection multiple */}
                  <div className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center ${
                    selectedChoices.some(selected => selected.id === choice.id)
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedChoices.some(selected => selected.id === choice.id) && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className={`font-medium text-gray-800 mb-1 ${isMobile ? 'text-sm' : ''}`}>
                      {choice.label}
                    </div>
                    <div className={`text-gray-600 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                      {choice.description}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* AJOUTÉ : Bouton de validation si plusieurs choix sélectionnés */}
          {selectedChoices.length > 0 && (
            <div className="mt-3 mb-3">
              <button
                onClick={handleValidateSelection}
                className="w-full p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Valider {selectedChoices.length} choix
              </button>
            </div>
          )}

          {/* Option personnalisée */}
          {!showCustomField ? (
            <button
              onClick={() => setShowCustomField(true)}
              className="w-full p-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-600 transition-all"
            >
              <Edit3 className="h-4 w-4 inline mr-2" />
              Répondre autrement...
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={localCustomText}
                onChange={(e) => setLocalCustomText(e.target.value)}
                placeholder="Votre réponse personnalisée..."
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleCustomSubmit()}
                autoFocus
              />
              <button
                onClick={handleCustomSubmit}
                disabled={!localCustomText.trim()}
                className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPOSANT OBJECTIF AVEC CONVERSATION INTÉGRÉE
// ============================================================================

const ObjectiveCreationWithConversation = ({ 
  onSubmit, 
  onBack, 
  isMobile, 
  isTablet,
  userId 
}) => {
  // États pour la conversation
  const [conversationMode, setConversationMode] = useState('formulation');
  const [currentObjective, setCurrentObjective] = useState('');
  const [freeMessages, setFreeMessages] = useState([]);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  
  // États pour les questions guidées
  const [aiQuestions, setAiQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  
  // Variables calculées
  const guidedAnswersCount = Object.keys(answers).length;
  
  // MODIFIÉ : mise à jour de la logique de vérification
  const allQuestionsAnswered = aiQuestions.length > 0 && aiQuestions.every(q => 
    answers[q.id] && (
      (answers[q.id].type === 'choices' && answers[q.id].data.length > 0) ||
      (answers[q.id].type === 'custom')
    )
  );
  
  const canCreateObjective = currentObjective.trim().length > 10;

  // ============================================================================
  // MISE À JOUR AUTOMATIQUE DE L'OBJECTIF
  // ============================================================================

  // Effet pour mettre à jour automatiquement l'objectif quand les réponses changent
  useEffect(() => {
    const updateObjectiveAutomatically = async () => {
      if (Object.keys(answers).length > 0 && currentObjective) {
        try {
          const enrichedObjective = await enrichObjectiveFromAnswers(
            currentObjective, 
            answers, 
            aiQuestions, 
            askGPT
          );
          
          if (enrichedObjective !== currentObjective) {
            setCurrentObjective(enrichedObjective);
            setLastUpdateTime(new Date().toLocaleTimeString());
          }
        } catch (error) {
          console.error('Erreur mise à jour automatique objectif:', error);
        }
      }
    };

    // Délai pour éviter les appels trop fréquents
    const timer = setTimeout(updateObjectiveAutomatically, 1000);
    return () => clearTimeout(timer);
  }, [answers, aiQuestions]); // Retirer currentObjective des dépendances

  // ============================================================================
  // GESTIONNAIRES DE CONVERSATION LIBRE
  // ============================================================================

  const handleSendFreeMessage = async (messageText) => {
    if (!messageText.trim()) return;
    
    setIsLoadingChat(true);
    
    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      text: messageText,
      timestamp: Date.now()
    };
    
    const updatedMessages = [...freeMessages, userMessage];
    setFreeMessages(updatedMessages);
    
    try {
      const prompt = `
Tu es Aladinn, un assistant expert qui aide les utilisateurs à formuler et préciser leurs objectifs.

Objectif actuel de l'utilisateur : "${currentObjective}"

Nouveau message utilisateur : "${messageText}"

IMPORTANT - Instructions de réponse :
- NE DIS PAS "Bonjour", "Salut", "C'est super", ou autres formules de politesse
- Réponds directement et concrètement à la question
- Sois concis et utile (maximum 80 mots)
- Si l'utilisateur précise ou modifie son objectif, aide-le à l'enrichir
- Pose UNE question courte et pertinente si nécessaire

Format de réponse :
1. Réponse directe à la question
2. [Si applicable] Une question courte pour clarifier

Ton conversationnel mais direct.
`;

      const response = await askGPT(prompt);
      
      const botMessage = {
        id: `bot-${Date.now()}`,
        type: 'bot',
        text: response || "Pouvez-vous me donner plus de détails ?",
        timestamp: Date.now()
      };
      
      const finalMessages = [...updatedMessages, botMessage];
      setFreeMessages(finalMessages);
      
      // Analyser si le message contient une mise à jour d'objectif
      if (currentObjective) {
        const objectiveUpdatePrompt = `
Analyse ce message utilisateur : "${messageText}"
Objectif actuel : "${currentObjective}"

Le message contient-il une précision, modification ou enrichissement de l'objectif ?

Si OUI, génère un objectif enrichi intégrant naturellement les nouvelles informations.
Si NON, réponds "AUCUN_CHANGEMENT".

Réponds UNIQUEMENT par l'objectif enrichi OU "AUCUN_CHANGEMENT".
`;

        try {
          const objectiveAnalysis = await askGPT(objectiveUpdatePrompt);
          
          if (objectiveAnalysis && 
              objectiveAnalysis.trim() !== "AUCUN_CHANGEMENT" && 
              objectiveAnalysis.trim() !== currentObjective.trim() &&
              objectiveAnalysis.length > 10) {
            
            setCurrentObjective(objectiveAnalysis.trim());
            setLastUpdateTime(new Date().toLocaleTimeString());
          }
        } catch (error) {
          console.error('Erreur analyse objectif:', error);
        }
      }
      
    } catch (error) {
      console.error('Erreur chat libre:', error);
      const errorMessage = {
        id: `bot-error-${Date.now()}`,
        type: 'bot',
        text: "Désolé, j'ai rencontré un problème. Pouvez-vous reformuler votre question ?",
        timestamp: Date.now()
      };
      
      const finalMessages = [...updatedMessages, errorMessage];
      setFreeMessages(finalMessages);
      
    } finally {
      setIsLoadingChat(false);
    }
  };

  // ============================================================================
  // GESTIONNAIRES DE QUESTIONS GUIDÉES
  // ============================================================================

  const generateQuestionsFromObjective = async () => {
    if (!currentObjective.trim()) return;
    
    setIsLoadingQuestions(true);

    try {
      const questions = await generateGuidedQuestionsForObjective(currentObjective, askGPT);
      setAiQuestions(questions);
      setConversationMode('guided');
    } catch (error) {
      console.error('Erreur génération questions:', error);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  // MODIFIÉ : gestionnaire pour la sélection multiple
  const handleChoiceSelect = (questionId, choices) => {
    const newAnswers = {
      ...answers,
      [questionId]: { 
        type: 'choices', 
        data: Array.isArray(choices) ? choices : [choices]
      }
    };
    setAnswers(newAnswers);
  };

  const handleCustomTextSubmit = (questionId, text) => {
    const newAnswers = {
      ...answers,
      [questionId]: { type: 'custom', data: { label: text, description: 'Réponse personnalisée' } }
    };
    setAnswers(newAnswers);
  };

  // ============================================================================
  // GESTIONNAIRES DE NAVIGATION
  // ============================================================================

  const handleModeChange = (newMode) => {
    if (newMode === 'guided' && currentObjective.trim()) {
      setConversationMode('generating');
      generateQuestionsFromObjective();
    } else {
      setConversationMode(newMode);
    }
  };

  const handleObjectiveUpdate = (newObjective) => {
    setCurrentObjective(newObjective);
    setLastUpdateTime(new Date().toLocaleTimeString());
  };

  const handleStartConversation = () => {
    if (!currentObjective.trim()) {
      alert('Veuillez d\'abord saisir un objectif initial');
      return;
    }
    setConversationMode('generating');
    generateQuestionsFromObjective();
  };

  const handleBackToFormulation = () => {
    setConversationMode('formulation');
    setFreeMessages([]);
    setAiQuestions([]);
    setAnswers({});
  };

  const handleCreateFinalObjective = async () => {
    if (!canCreateObjective) return;
    
    setIsCreating(true);
    
    // Préparer les données de conversation
    const conversationData = {
      initialObjective: currentObjective,
      
      guidedQuestions: aiQuestions,
      guidedAnswers: answers,
      enrichedFromAnswers: allQuestionsAnswered
    };
    
    // Utiliser la logique existante pour créer l'objectif
    const objectiveData = {
      objectif: currentObjective,
      type: 'project',
      conversationData: conversationData
    };
    
    try {
      await onSubmit(objectiveData);
    } catch (error) {
      console.error('Erreur création objectif:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // ============================================================================
  // RENDU PRINCIPAL
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Étape 1: Formulation initiale de l'objectif */}
      {conversationMode === 'formulation' && (
        <div className="space-y-6">
          <ObjectifEditor
            objectif={currentObjective}
            onSave={handleObjectiveUpdate}
            lastUpdate={lastUpdateTime}
            conversationData={{}}
          />
          
          {currentObjective.trim() && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-green-800 mb-2">
                    Prêt à préciser votre objectif ?
                  </h3>
                  <p className="text-green-700 text-sm">
                    Aladinn va générer 3 questions ciblées pour vous aider à clarifier et enrichir votre objectif.
                  </p>
                </div>
                <button
                  onClick={handleStartConversation}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                >
                  <Target className="h-5 w-5 mr-2" />
                  Répondre aux questions
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Étape 2: Génération et réponse aux questions guidées */}
      {(conversationMode === 'generating' || conversationMode === 'guided') && (
        <div className="space-y-6">
          {/* Objectif actuel en en-tête */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-600 mb-1">Objectif actuel</h3>
                <p className="text-gray-800 leading-relaxed">{currentObjective}</p>
                {lastUpdateTime && (
                  <p className="text-xs text-green-600 mt-1">
                    Mis à jour à {lastUpdateTime}
                  </p>
                )}
              </div>
              <button
                onClick={handleBackToFormulation}
                className="ml-4 px-3 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit3 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Mode génération de questions */}
          {conversationMode === 'generating' && (
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <div className="text-center">
                <div className="flex items-center justify-center mb-4">
                  <Lightbulb className="h-8 w-8 text-blue-500 mr-3" />
                  {isLoadingQuestions && <Loader2 className="h-8 w-8 animate-spin text-blue-500" />}
                </div>
                
                {isLoadingQuestions ? (
                  <div>
                    <h3 className="font-medium text-blue-800 mb-2">Analyse en cours...</h3>
                    <p className="text-sm text-blue-700">
                      Aladinn analyse votre objectif pour générer des questions pertinentes.
                    </p>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium text-blue-800 mb-2">Questions prêtes</h3>
                    <p className="text-sm text-blue-700">
                      3 questions ont été générées pour préciser votre objectif.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Mode questions guidées */}
          {conversationMode === 'guided' && aiQuestions.length > 0 && (
            <div className="space-y-4">
              {/* Questions */}
              {aiQuestions.map((question, index) => (
                <AIGeneratedQuestion
                  key={question.id}
                  question={question}
                  questionIndex={index}
                  totalQuestions={aiQuestions.length}
                  selectedChoices={answers[question.id]?.type === 'choices' ? answers[question.id].data : []}
                  customText={answers[question.id]?.type === 'custom' ? answers[question.id].data.label : ''}
                  onChoiceSelect={(choices) => handleChoiceSelect(question.id, choices)}
                  onCustomTextSubmit={(text) => handleCustomTextSubmit(question.id, text)}
                  isCompleted={!!(answers[question.id] && (
                    (answers[question.id].type === 'choices' && answers[question.id].data.length > 0) ||
                    (answers[question.id].type === 'custom')
                  ))}
                  isMobile={isMobile}
                />
              ))}

              {/* Indication de mise à jour automatique */}
              {allQuestionsAnswered && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                    <div>
                      <h3 className="font-medium text-green-800 mb-1">
                        Questions complétées
                      </h3>
                      <p className="text-sm text-green-700">
                        Votre objectif a été automatiquement enrichi avec vos réponses.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions finales */}
          <div className="flex items-center justify-between p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <h3 className="font-medium text-gray-800 mb-1">Prêt à créer votre objectif ?</h3>
              <p className="text-sm text-gray-600">
                {canCreateObjective 
                  ? "Votre objectif semble bien défini. Vous pouvez le finaliser."
                  : "Votre objectif a besoin d'être plus détaillé."
                }
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onBack}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2 inline" />
                Retour
              </button>
              <button
                onClick={handleCreateFinalObjective}
                disabled={!canCreateObjective || isCreating}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <ThumbsUp className="h-4 w-4 mr-2" />
                    Créer l'objectif
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// UTILITAIRES DE TRAITEMENT DES DONNÉES
// ============================================================================

const processObjectiveData = (objective) => {
  let realActionsCount = 0;
  let completedActionsCount = 0;
  
  if (objective.actionPlan?.steps && Array.isArray(objective.actionPlan.steps)) {
    realActionsCount = objective.actionPlan.steps.length;
    completedActionsCount = objective.actionPlan.steps.filter(step => step.termine === true).length;
  } else if (objective.recommendation?.actions && Array.isArray(objective.recommendation.actions)) {
    realActionsCount = objective.recommendation.actions.length;
    completedActionsCount = objective.recommendation.actions.filter(action => action.completed === true).length;
  }

  const actionProgress = realActionsCount > 0 ? Math.round((completedActionsCount / realActionsCount) * 100) : 0;

  return {
    ...objective,
    actionsCount: realActionsCount,
    completedActionsCount: completedActionsCount,
    performance: objective.globalProgress || actionProgress || objective.performance || 0
  };
};

const filterObjectives = (objectives, { searchQuery, filterType, filterTheme }) => {
  return objectives.filter(objective => {
    const domain = objective.chapitre || objective.theme || 'Non classé';
    
    const matchesSearch = !searchQuery || 
      objective.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      objective.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      domain.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = filterType === 'all' || objective.type === filterType;
    const matchesTheme = filterTheme === 'all' || domain === filterTheme;
    
    return matchesSearch && matchesType && matchesTheme;
  });
};

const calculateStats = (objectives) => {
  const domainStats = {};
  let totalActions = 0;
  let completedActions = 0;

  objectives.forEach(obj => {
    const domain = obj.chapitre || obj.theme || 'Non classé';
    if (!domainStats[domain]) {
      domainStats[domain] = { count: 0, actions: 0, completedActions: 0 };
    }
    domainStats[domain].count++;
    
    const actions = obj.actionsCount || 0;
    const completed = obj.completedActionsCount || 0;
    
    domainStats[domain].actions += actions;
    domainStats[domain].completedActions += completed;
    totalActions += actions;
    completedActions += completed;
  });

  return { domainStats, totalActions, completedActions };
};

// ============================================================================
// MODULE PRINCIPAL AMBITION - VERSION SIMPLIFIÉE
// ============================================================================

const ObjectiveModule = React.memo(({ 
  step, 
  userId, 
  currentCompetence, 
  onSelectCompetence, 
  userObjectives, 
  onNavigateToStep,
  onUpdateObjectives
}) => {
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  
  // États principaux
  const [ambitionMode, setAmbitionMode] = useState('create');
  const [isCreating, setIsCreating] = useState(false);

  // Mise à jour du mode selon la compétence actuelle
  useEffect(() => {
    if (currentCompetence) {
      setAmbitionMode('view');
    } else {
      setAmbitionMode('create');
    }
  }, [currentCompetence]);

  // Traitement des objectifs avec vraies données
  const processedObjective = useMemo(() => {
    return currentCompetence ? processObjectiveData(currentCompetence) : null;
  }, [currentCompetence]);

  const processedOtherObjectives = useMemo(() => {
    return userObjectives
      .filter(obj => obj.id !== currentCompetence?.id)
      .map(processObjectiveData)
      .slice(0, isMobile ? 2 : 3);
  }, [userObjectives, currentCompetence?.id, isMobile]);

  // ============================================================================
  // LOGIQUE D'INTÉGRATION IA ET FIREBASE AMÉLIORÉE
  // ============================================================================

  const processObjectiveWithAI = async (objectiveData) => {
    try {
      // 1. Analyse et structuration par IA
      const analysisPrompt = `En tant qu'expert en développement personnel et professionnel, analyse cet objectif et génère une structure complète.

Objectif utilisateur : "${objectiveData.objectif}"
Type : ${objectiveData.type}

${objectiveData.conversationData ? `
Données de conversation disponibles :
- Messages libres : ${objectiveData.conversationData.freeMessages ? objectiveData.conversationData.freeMessages.length : 0}
- Questions guidées : ${objectiveData.conversationData.guidedQuestions ? objectiveData.conversationData.guidedQuestions.length : 0}
- Enrichi par réponses : ${objectiveData.conversationData.enrichedFromAnswers ? 'Oui' : 'Non'}
` : ''}

Génère une réponse au format JSON strict avec cette structure :
{
  "title": "Titre concis et motivant de l'objectif (max 80 caractères)",
  "description": "Description structurée et inspirante de l'objectif",
  "chapitre": "Domaine principal parmi : Professionnel, Personnel, Educatif, Business, Activités ludique et créative, Développement entrepreneurial, Formation et apprentissage, Évolution de carrière, Santé et bien-être, Objectifs Personnels, Voyages, Gestion immobilière, Finance et Banque, Activités sportives",
  "motsCles": ["mot1", "mot2", "mot3", "mot4", "mot5"],
  "difficulte": "Débutant|Intermédiaire|Avancé",
  "dureeEstimee": "Estimation réaliste de la durée (ex: 3 mois)",
  "priorite": "Basse|Moyenne|Haute"
}

Assure-toi que :
- Le titre soit accrocheur et motivant
- La description inspire l'action
- Le domaine corresponde exactement à un des domaines listés
- Les mots-clés soient pertinents pour la recherche
- L'estimation soit réaliste`;

      const analysisResult = await askGPT(analysisPrompt);
      
      // Parser la réponse JSON
      let structuredData;
      try {
        const jsonMatch = analysisResult.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          structuredData = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('Format JSON non détecté');
        }
      } catch (parseError) {
        console.error('Erreur de parsing JSON:', parseError);
        structuredData = {
          title: objectiveData.objectif.length > 80 ? 
            objectiveData.objectif.substring(0, 77) + '...' : 
            objectiveData.objectif,
          description: objectiveData.objectif,
          chapitre: 'Personnel',
          motsCles: ['objectif', 'développement', 'personnel'],
          difficulte: 'Intermédiaire',
          dureeEstimee: '3 mois',
          priorite: 'Moyenne'
        };
      }

      // 2. Génération de la stratégie avec contexte conversationnel
      let conversationContext = '';
      if (objectiveData.conversationData) {
        if (objectiveData.conversationData.freeMessages && objectiveData.conversationData.freeMessages.length > 0) {
          conversationContext += `\n\nContexte de conversation libre :\n${objectiveData.conversationData.freeMessages.map(m => `${m.type}: ${m.text}`).join('\n')}`;
        }
        
        if (objectiveData.conversationData.guidedAnswers && Object.keys(objectiveData.conversationData.guidedAnswers).length > 0) {
          const answers = Object.values(objectiveData.conversationData.guidedAnswers);
          conversationContext += `\n\nRéponses aux questions guidées :\n${answers.map(a => `- ${a.data.label}: ${a.data.description || ''}`).join('\n')}`;
        }
      }

      const strategyPrompt = `Tu es un coach, conseiller spécialisé en développement personnel, éducatif et professionnel.
Ton rôle est d'informer l'étudiant et l'aider à réaliser son projet. 
Adresse toi directement à l'étudiant. 

Format de réponse obligatoire avec balises [SECTION1], [SECTION2], [SECTION3] :

[SECTION1]
{Présente le sujet et le contexte à l'étudiant, si l'étudiant à posé une question répond à sa question, sans titre ni introduction. En 100 mots max}

[SECTION2]
{Propose une démarche pour réaliser cette action, ou cet objectif. Préciser les questions ou les orientations qui restent à définir par l'utilisateur. Si le sujet n'est pas suffisament explicite, pose une ou deux questions pour clarifier le sens et l'objectif de l'étudiant, sans titre ni introduction. en 100 mots max. }

[SECTION3] 
{Formule tes recommandations et propositions et invite l'étudiant à poursuivre dans l'espace inspiration, sans titre ni introduction, en 70 mots max}

Important :
- Utilise le markdown pour le formatage
- Mets les mots clés en **gras** ou *italique*
- Utilise $...$ pour les formules en ligne, $...$  pour les formules affichées

Voici l'objectif de l'étudiant : "${structuredData.title}"
Description : "${structuredData.description}"

${conversationContext}`;

      const recommendationResult = await askGPT(strategyPrompt);

      // 3. Sauvegarde en Firebase avec données conversationnelles
      const newObjective = {
        ...structuredData,
        type: objectiveData.type,
        objectifOriginal: objectiveData.objectif,
        recommendation: recommendationResult,
        recommendationCreatedAt: new Date().toISOString(),
        
        // Données conversationnelles enrichies
        conversationData: objectiveData.conversationData || {},
        conversationEnriched: !!objectiveData.conversationData,
        
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userId: userId,
        status: 'active'
      };

      const docRef = await addDoc(
        collection(db, 'users', userId, 'competences'),
        newObjective
      );

      const createdObjective = {
        id: docRef.id,
        ...newObjective,
        recommendation: recommendationResult
      };

      // Mise à jour de l'état
      if (onUpdateObjectives) {
        onUpdateObjectives(prev => [createdObjective, ...prev]);
      }

      // Sélectionner l'objectif créé et rester sur la page Ambition pour consultation
      onSelectCompetence(createdObjective);
      setAmbitionMode('view');
      
      return createdObjective;
      
    } catch (error) {
      console.error('Erreur lors du traitement de l\'objectif:', error);
      throw error;
    }
  };

  // ============================================================================
  // HANDLERS DE NAVIGATION
  // ============================================================================

  const handleSelectObjective = useCallback((objective) => {
    if (objective === 'list') {
      setAmbitionMode('list');
    } else {
      onSelectCompetence(objective);
      setAmbitionMode('view');
    }
  }, [onSelectCompetence]);

  const handleCreateObjective = useCallback(() => {
    setAmbitionMode('create');
  }, []);

  const handleBackToSelection = useCallback(() => {
    setAmbitionMode('selection');
  }, []);

  const handleObjectiveSubmit = useCallback(async (objectiveData) => {
    setIsCreating(true);
    
    try {
      await processObjectiveWithAI(objectiveData);
    } catch (error) {
      console.error("Erreur lors de la création de l'objectif:", error);
    } finally {
      setIsCreating(false);
    }
  }, [processObjectiveWithAI]);

  const handleNavigateToStrategy = useCallback(() => {
    onNavigateToStep('strategy');
  }, [onNavigateToStep]);

  const handleUpdateObjective = useCallback((updatedObjective) => {
    if (onUpdateObjectives) {
      onUpdateObjectives(prev => 
        prev.map(obj => obj.id === updatedObjective.id ? updatedObjective : obj)
      );
    }
    onSelectCompetence(updatedObjective);
  }, [onUpdateObjectives, onSelectCompetence]);

  // ============================================================================
  // RENDU PRINCIPAL
  // ============================================================================

  return (
    <div className={`space-y-6 ${isMobile ? 'px-2' : ''}`}>

      {/* Rendu conditionnel selon le mode */}
      {ambitionMode === 'selection' && (
        <ObjectiveSelection
          userObjectives={userObjectives}
          onSelectObjective={handleSelectObjective}
          onCreateObjective={handleCreateObjective}
          isMobile={isMobile}
          isTablet={isTablet}
          processObjectiveData={processObjectiveData}
          getDomainInfo={getDomainInfo}
        />
      )}

      {ambitionMode === 'list' && (
        <div className={`bg-white rounded-xl shadow-lg border border-gray-200 ${isMobile ? 'p-4' : 'p-8'}`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-800`}>
              {isMobile ? 'Choisir un objectif' : 'Choisir un objectif existant'}
            </h3>
            <button
              onClick={handleBackToSelection}
              className={`text-purple-600 hover:text-purple-800 font-medium flex items-center gap-2 ${isMobile ? 'text-sm' : ''}`}
            >
              <ArrowLeft className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
              Retour
            </button>
          </div>

          <ObjectiveSearchForm
            userObjectives={userObjectives}
            onSelectObjective={handleSelectObjective}
            onBack={handleBackToSelection}
            isMobile={isMobile}
            isTablet={isTablet}
            processObjectiveData={processObjectiveData}
            getDomainInfo={getDomainInfo}
            calculateStats={calculateStats}
            filterObjectives={filterObjectives}
          />
        </div>
      )}

      {ambitionMode === 'create' && (
        <div className={`bg-white rounded-xl shadow-lg border border-gray-200 ${isMobile ? 'p-4' : 'p-6'}`}>
          {/* Header simplifié - sans redondance */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-800 mb-1`}>
                Définissez votre objectif
              </h3>
              <p className={`text-gray-600 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                Commencez par décrire ce que vous souhaitez accomplir
              </p>
            </div>
            <button
              onClick={handleBackToSelection}
              className={`text-purple-600 hover:text-purple-800 font-medium flex items-center gap-2 ${isMobile ? 'text-sm' : ''}`}
            >
              <ArrowLeft className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
              {!isMobile && 'Retour'}
            </button>
          </div>

          {isCreating ? (
            <div className={`text-center ${isMobile ? 'py-8' : 'py-12'}`}>
              <Loader2 className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} animate-spin text-purple-500 mx-auto mb-4`} />
              <h4 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-gray-800 mb-2`}>Analyse de votre ambition</h4>
              <p className={`text-gray-600 ${isMobile ? 'text-sm px-4' : ''}`}>
                Notre IA structure votre objectif et prépare vos recommandations...
              </p>
            </div>
          ) : (
            <ObjectiveCreationWithConversation
              onSubmit={handleObjectiveSubmit}
              onBack={handleBackToSelection}
              isMobile={isMobile}
              isTablet={isTablet}
              userId={userId}
            />
          )}
        </div>
      )}

      {ambitionMode === 'view' && currentCompetence && (
        <ObjectiveView
          objective={currentCompetence}
          userObjectives={userObjectives}
          onSelectObjective={handleSelectObjective}
          onNavigateToStrategy={handleNavigateToStrategy}
          onCreateObjective={handleCreateObjective}
          onUpdateObjective={handleUpdateObjective}
          userId={userId}
          isMobile={isMobile}
          processedObjective={processedObjective}
          processedOtherObjectives={processedOtherObjectives}
          getDomainInfo={getDomainInfo}
        />
      )}
    </div>
  );
});

ObjectiveModule.displayName = 'ObjectiveModule';

export default ObjectiveModule;
